import { useEffect, useRef, useState } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import type { Set } from '@/types/cardCatalog'
import { updateSetAlternativeName } from '@/services/cardService'
import { Loader2, Pencil } from 'lucide-react'

function formatDate(date: Date | null | undefined): string {
  if (!date) return '—'
  try {
    if (Number.isNaN(date.getTime())) return '—'
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  } catch {
    return '—'
  }
}

export type CardDatabaseSetsTableProps = {
  items: Set[]
  /** Called after a successful alternative-name save so the parent can refetch. */
  onSetsUpdated?: (newSet: Set) => void
}

export function CardDatabaseSetsTable({ items, onSetsUpdated }: CardDatabaseSetsTableProps) {
  const [savingId, setSavingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const editInputRef = useRef<HTMLInputElement>(null)
  const skipBlurCommitRef = useRef(false)

  useEffect(() => {
    if (editingId) {
      editInputRef.current?.focus()
      editInputRef.current?.select()
    }
  }, [editingId])

  /** `true` if saved or nothing to save; `false` if the request failed. */
  async function commitAlternativeName(row: Set, value: string): Promise<boolean> {
    const next = value.trim() === '' ? null : value.trim()
    const prev = row.alternativeName ?? null
    if (next === prev) {
      return true
    }
    setSavingId(row.id)
    setError(null)
    try {
      const newSet = await updateSetAlternativeName(row.id, next)
      onSetsUpdated?.(newSet)
      return true
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save')
      return false
    } finally {
      setSavingId(null)
    }
  }

  return (
    <div className="space-y-2">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[72px]">Locale</TableHead>
              <TableHead className="w-[100px]">Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Series</TableHead>
              <TableHead className="min-w-[180px]">Alt. name</TableHead>
              <TableHead className="w-[120px]">Released</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  No sets match this search.
                </TableCell>
              </TableRow>
            ) : (
              items.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-mono text-xs uppercase">{row.locale}</TableCell>
                  <TableCell className="font-mono text-xs">{row.code}</TableCell>
                  <TableCell className="max-w-[220px] truncate font-medium" title={row.name}>
                    {row.name}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm max-w-[180px] truncate" title={row.series}>
                    {row.series}
                  </TableCell>
                  <TableCell className="p-1 align-middle">
                    {editingId === row.id ? (
                      <div className="flex items-center gap-2 min-w-[160px] max-w-[260px]">
                        <Input
                          ref={editInputRef}
                          key={`${row.id}-${row.updatedAt?.getTime?.() ?? 0}-${row.alternativeName ?? ''}`}
                          className="h-8 text-sm flex-1 min-w-0"
                          defaultValue={row.alternativeName ?? ''}
                          disabled={savingId === row.id}
                          aria-label={`Alternative name for ${row.name}`}
                          onBlur={async (e) => {
                            if (skipBlurCommitRef.current) {
                              skipBlurCommitRef.current = false
                              return
                            }
                            const ok = await commitAlternativeName(row, e.target.value)
                            if (ok) setEditingId(null)
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              e.preventDefault()
                              skipBlurCommitRef.current = true
                              setEditingId(null)
                            }
                            if (e.key === 'Enter') {
                              skipBlurCommitRef.current = false
                              e.currentTarget.blur()
                            }
                          }}
                        />
                        {savingId === row.id && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" aria-hidden />}
                      </div>
                    ) : (
                      <div className="group flex items-center gap-1 min-w-[160px] max-w-[260px]">
                        <span className="text-sm text-muted-foreground truncate flex-1 min-w-0" title={row.alternativeName ?? undefined}>
                          {row.alternativeName?.trim() ? row.alternativeName : '—'}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 shrink-0 opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
                          aria-label={`Edit alternative name for ${row.name}`}
                          disabled={savingId === row.id}
                          onClick={() => setEditingId(row.id)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="tabular-nums text-sm">{formatDate(row.releasedAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
